import { v4 as uuidv4 } from 'uuid';
import { message } from 'ant-design-vue';
import apiAxios from '../../config/request.js';
import config from '../../config/config.js';
import { USE_LOCAL_IMAGE_MOCK, createLocalImageItem } from './localImage.js';
import { USE_LOCAL_FILE_MOCK, createLocalFileItem, formatFileSize } from './localFile.js';
import {
	USE_LOCAL_AUDIO_MOCK,
	createLocalAudioItem,
	formatAudioDuration,
} from './localAudio.js';
import {
	USE_LOCAL_VIDEO_MOCK,
	createLocalVideoItem,
	formatVideoDuration,
} from './localVideo.js';
import { pickFilesizeByte, pickFilesizeLabel } from './mediaItem.js';
import { getQiniuSignedUrl } from './qiniuMedia.js';

export const HEADER_MEDIA_UPLOAD_TYPES = {
	image: 'img',
	file: 'file',
	audio: 'audio',
	video: 'video',
};

/** 高级编辑器 dev 环境走独立测试目录，可在 config.geditUploadTestPrefix 修改 */
export const GEDIT_UPLOAD_KEY_PREFIX = config.devEnv === 'dev'
	? (config.geditUploadTestPrefix || 'mbm_test_gedit/')
	: '';

export function isMockHeaderUpload(type) {
	switch (type) {
		case 'image':
			return USE_LOCAL_IMAGE_MOCK;
		case 'file':
			return USE_LOCAL_FILE_MOCK;
		case 'audio':
			return USE_LOCAL_AUDIO_MOCK;
		case 'video':
			return USE_LOCAL_VIDEO_MOCK;
		default:
			return false;
	}
}

function resolveApiRequest(vm) {
	return vm?.$apiRequest || apiAxios;
}

/** uploadMediaToQiniu 需要可挂 qiniu_token、带 $message 的宿主 */
function resolveUploadHost(vm) {
	const host = vm && typeof vm === 'object' ? vm : {};
	if (!host.$message) {
		host.$message = message;
	}
	return host;
}

function sleep(ms) {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

async function simulateUploadProgress(file, onProgress) {
	const steps = [12, 28, 46, 63, 81, 94, 100];
	for (const percent of steps) {
		onProgress?.({
			percent,
			filename: file.name,
		});
		if (percent < 100) {
			await sleep(180);
		}
	}
}

function mapUploadResponseToItem(type, file, res = {}) {
	const rawUrl = res.transcode_url || res.url  || res.old_transcode_url || '';
	const filesizeByte = pickFilesizeByte(res) || (file?.size || 0);
	const signedPreview = rawUrl ? getQiniuSignedUrl(rawUrl) : '';
	const base = {
		id: res.id || res.fileId || uuidv4(),
		filename: res.filename || file.name || '',
		filesize: pickFilesizeLabel(res, filesizeByte) || formatFileSize(file?.size || 0),
		filesize_byte: filesizeByte,
		url: rawUrl,
		previewUrl: signedPreview,
		local: false,
	};

	if (type === 'image') {
		return base;
	}

	if (type === 'audio') {
		const duration = res.duration || 0;
		return {
			...base,
			duration,
			durationText: formatAudioDuration(duration),
			previewUrl: signedPreview || (file ? URL.createObjectURL(file) : ''),
		};
	}

	if (type === 'video') {
		const duration = res.duration || 0;
		return {
			...base,
			duration,
			durationText: formatVideoDuration(duration),
			previewUrl: signedPreview || (file ? URL.createObjectURL(file) : ''),
		};
	}

	return base;
}

async function createLocalItem(type, file) {
	if (type === 'image') {
		return createLocalImageItem(file);
	}
	if (type === 'file') {
		return createLocalFileItem(file);
	}
	if (type === 'audio') {
		return createLocalAudioItem(file);
	}
	if (type === 'video') {
		return createLocalVideoItem(file);
	}
	return createLocalFileItem(file);
}

async function uploadSingleFile(vm, type, file, index, onProgress) {
	const uploadType = HEADER_MEDIA_UPLOAD_TYPES[type] || 'file';

	if (isMockHeaderUpload(type)) {
		await simulateUploadProgress(file, (payload) => {
			onProgress?.({ ...payload, index });
		});
		return createLocalItem(type, file);
	}

	const apiRequest = resolveApiRequest(vm);
	const uploadFn = apiRequest?.uploadMediaToQiniu;
	if (typeof uploadFn !== 'function') {
		const err = new Error('上传服务未初始化');
		message.error(err.message);
		throw err;
	}

	const host = resolveUploadHost(vm);
	const uploadExtra = {
		upload_type: uploadType,
		index,
	};
	if (GEDIT_UPLOAD_KEY_PREFIX) {
		uploadExtra.upload_key_prefix = GEDIT_UPLOAD_KEY_PREFIX;
	}

	return new Promise((resolve, reject) => {
		uploadFn.call(apiRequest, host, file, (progress) => {
			onProgress?.({
				percent: Math.floor(progress.percent || 0),
				filename: file.name,
				index,
			});
		}, null, uploadExtra).then((res) => {
			resolve(mapUploadResponseToItem(type, file, res));
		}).catch(reject);
	});
}

export function createUploadTasks(files = []) {
	return files.map((file, index) => ({
		id: `${index}-${file.name}`,
		filename: file.name,
		percent: 0,
	}));
}

export function updateUploadTaskProgress(tasks, { index, percent, filename }) {
	if (typeof index !== 'number' || !tasks[index]) {
		return;
	}
	tasks[index].percent = percent;
	if (filename) {
		tasks[index].filename = filename;
	}
}

export function getMergedUploadPercent(tasks = []) {
	if (!tasks.length) return 0;
	if (tasks.length === 1) return tasks[0].percent || 0;

	const total = tasks.length;
	let completed = 0;
	let currentPercent = 0;

	for (let i = 0; i < tasks.length; i += 1) {
		if ((tasks[i].percent || 0) >= 100) {
			completed += 1;
		} else {
			currentPercent = tasks[i].percent || 0;
			break;
		}
	}

	if (completed >= total) return 100;
	return Math.floor((completed * 100 + currentPercent) / total);
}

export function getMergedUploadFilename(tasks = []) {
	if (!tasks.length) return '';
	if (tasks.length === 1) return tasks[0].filename || '';

	const current = tasks.find((task) => (task.percent || 0) < 100) || tasks[tasks.length - 1];
	const filename = current?.filename || tasks[0].filename || '';
	return `${filename} 等${tasks.length}个`;
}

export function getMergedUploadDisplay(tasks = []) {
	return {
		id: 'merged',
		filename: getMergedUploadFilename(tasks),
		percent: getMergedUploadPercent(tasks),
	};
}

export async function uploadHeaderMediaFiles(vm, { type, files = [], onProgress } = {}) {
	const results = [];
	for (let index = 0; index < files.length; index += 1) {
		const item = await uploadSingleFile(vm, type, files[index], index, onProgress);
		results.push(item);
	}
	return results;
}
